import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { useNetwork } from '../contexts/NetworkContext'
import { NetworkCell } from '@/components/TableCells'
import type { NetworkRef } from '@/services/core/networkRef'

/** A list row that may have come from a network other than the primary one. */
interface NetworkScopedRow {
  network?: NetworkRef
}

/**
 * Support for advertising lists that span several networks (see the `network`
 * query parameter in services/context.ts).
 *
 * Gives back a Network column to splice into the table's column list — empty
 * while only the primary network is in scope, so single-network use is
 * unchanged — and a row-click handler that switches the app into a row's own
 * network before navigating, since detail pages and saves only ever resolve
 * against the primary context.
 */
export function useNetworkColumn<T extends NetworkScopedRow>(pathFor: (row: T) => string) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentNetwork, extraNetworkTagIds, switchNetwork } = useNetwork()
  const primaryTagId = currentNetwork?.tagId
  const spansNetworks = extraNetworkTagIds.length > 0

  const columns = useMemo<ColumnDef<T, unknown>[]>(() => {
    if (!spansNetworks) return []
    return [
      {
        id: 'network',
        header: t('network.column'),
        size: 150,
        enableSorting: false,
        cell: ({ row }) => (
          <NetworkCell
            network={row.original.network}
            isPrimary={row.original.network?.tagId === primaryTagId}
            title={t('network.switchToEdit', { name: row.original.network?.name ?? '' })}
          />
        ),
      },
    ]
  }, [spansNetworks, primaryTagId, t])

  const onRowClick = useCallback(
    (row: T) => {
      const tagId = row.network?.tagId
      if (spansNetworks && tagId && tagId !== primaryTagId) {
        // switchNetwork reloads the page, so hand it the destination first.
        switchNetwork(tagId, pathFor(row))
        return
      }
      navigate(pathFor(row))
    },
    [spansNetworks, primaryTagId, switchNetwork, navigate, pathFor],
  )

  return { networkColumns: columns, onRowClick, spansNetworks }
}
